/**
 * @fileoverview Game Stats Bar Component
 * @description Shows timer, moves, and matched pairs for memory game
 */

import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";

interface GameStatsBarProps {
  moves: number;
  matchedPairs: number;
  totalPairs: number;
  elapsedTime: number;
  isPaused?: boolean;
}

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};

/**
 * GameStatsBar - Displays game statistics
 */
export const GameStatsBar: React.FC<GameStatsBarProps> = ({
  moves,
  matchedPairs,
  totalPairs,
  elapsedTime,
  isPaused = false,
}) => {
  const progress = totalPairs > 0 ? matchedPairs / totalPairs : 0;

  return (
    <View style={styles.container}>
      {/* Stats Row */}
      <View style={styles.statsRow}>
        {/* Timer */}
        <View style={styles.statItem}>
          <Ionicons
            name="time-outline"
            size={20}
            color={isPaused ? "#F59E0B" : "#8B5CF6"}
          />
          <Text style={[styles.statValue, isPaused && styles.pausedText]}>
            {formatTime(elapsedTime)}
          </Text>
          <Text style={styles.statLabel}>Tijd</Text>
        </View>

        {/* Moves */}
        <View style={styles.statItem}>
          <Ionicons name="swap-horizontal" size={20} color="#3B82F6" />
          <Text style={styles.statValue}>{moves}</Text>
          <Text style={styles.statLabel}>Zetten</Text>
        </View>

        {/* Pairs Found */}
        <View style={styles.statItem}>
          <Ionicons name="checkmark-done" size={20} color="#22C55E" />
          <Text style={styles.statValue}>
            {matchedPairs}/{totalPairs}
          </Text>
          <Text style={styles.statLabel}>Paren</Text>
        </View>
      </View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              { width: `${progress * 100}%` },
            ]}
          />
        </View>
        <Text style={styles.progressText}>
          {Math.round(progress * 100)}%
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#1E293B",
    borderRadius: 16,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  statsRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 12,
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statValue: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginTop: 4,
  },
  pausedText: {
    color: "#F59E0B",
  },
  statLabel: {
    fontSize: 11,
    color: "#9CA3AF",
    marginTop: 2,
  },
  progressContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  progressBar: {
    flex: 1,
    height: 8,
    backgroundColor: "#374151",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#22C55E",
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#22C55E",
    width: 40,
    textAlign: "right",
  },
});

export default GameStatsBar;

